<template>
    <section class="neighborhood-events container" v-if="eventCount > 0">
        <h2 class="headline-1">Events in this Neighborhood ({{ eventCount }})</h2>
        <NuxtLink :to="eventsLink" class="body-link neighborhood-events-link">View all free and public events</NuxtLink>
    </section>
</template>

<script setup>
    import { useEventsStore } from '@/stores/events';
    import { useSitesStore } from '@/stores/sites';

    const props = defineProps({
        neighborhoodId: {
            type: String,
            required: true,
        },
    });

    const route = useRoute();
    const isFramed = computed(() => route.path.includes('/iframe/'));

    const { eventsData } = storeToRefs(useEventsStore());
    const { sitesData } = storeToRefs(useSitesStore());

    // Events have no neighborhood of their own — they point at a Site, and the
    // site carries `OHC Neighborhood`. So "events in this neighborhood" means
    // events hosted at a site belonging to it.
    const siteIdsInNeighborhood = computed(() => {
        const ids = new Set();
        for (const site of sitesData.value || []) {
            if (site?.fields?.['OHC Neighborhood']?.[0] === props.neighborhoodId) ids.add(site.id);
        }
        return ids;
    });

    const eventCount = computed(() => {
        const ids = siteIdsInNeighborhood.value;
        if (!ids.size) return 0;
        // An event can list more than one Site, so match on any of them.
        return (eventsData.value?.records || []).filter((event) =>
            (event?.fields?.Site || []).some((siteId) => ids.has(siteId)),
        ).length;
    });

    const eventsLink = computed(() => ({
        path: isFramed.value ? '/iframe/events' : '/events',
        query: { neighborhood: props.neighborhoodId },
    }));
</script>

<style lang="scss" scoped>
    .neighborhood-events {
        // 30px top as well as bottom. This section had the bottom half only, so
        // it sat flush against the sites list above it while every other block
        // on the page separates itself the same way — like-a-local-carousel is
        // 30px top and bottom, nearby-neighborhoods is 30px top.
        //
        // Padding rather than margin, to match those siblings exactly and to
        // stay clear of margin collapsing between adjacent sections.
        padding: 30px 0;
    }

    .neighborhood-events-link {
        display: inline-block;
        margin-top: 12px;

        font-family: $body;
        font-size: 16px;
        font-weight: $book;
        line-height: 22px;

        // Same behaviour as the links in the homepage copy: black with an
        // underline at rest, red without one on hover (Asana O2-19). It was red
        // and underlined in both states.
        color: $black;

        &:hover,
        &:focus-visible {
            color: $red;
            text-decoration: none;
        }
    }
</style>
